import React, { useState, useEffect, useCallback, useRef } from 'react';
import './AdminProgramManagement.css';
import { validateQuestion, sanitizeInput } from '../utils/validation';
import config from '../config';

const { API_URL } = config;

const AdminQuestionManagement = ({ user, onMessage, preSelectedAssessment = '' }) => {
  const [questionForm, setQuestionForm] = useState({
    id: '', 
    assessment_name: preSelectedAssessment || '', 
    category: '', 
    question: '', 
    choices: ['', '', '', ''], 
    correct_answer: ''
  });
  const [allQuestions, setAllQuestions] = useState([]);
  const [filterAssessment, setFilterAssessment] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [assessmentNames, setAssessmentNames] = useState([]);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const loadingRef = useRef(false);
  
  const ITEMS_PER_PAGE = 20;

  const loadQuestions = useCallback(async () => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setQuestionsLoading(true);
    
    try {
      if (user?.token === 'demo-token-for-local-testing') {
        const demoQuestions = [
          {
            id: 'demo-q1',
            assessment_name: 'Cloud Operator Demo',
            category: 'DevOps',
            question: 'What is the primary benefit of using Infrastructure as Code (IaC)?',
            choices: ['Faster deployment times', 'Version control and reproducibility', 'Lower costs', 'Better security'],
            correct_answer: 'Version control and reproducibility'
          },
          {
            id: 'demo-q2',
            assessment_name: 'AWS Basics Demo',
            category: 'AWS',
            question: 'Which AWS service is primarily used for container orchestration?',
            choices: ['EC2', 'Lambda', 'EKS', 'S3'],
            correct_answer: 'EKS'
          }
        ];
        setAllQuestions(demoQuestions);
        setAssessmentNames(['Cloud Operator Demo', 'DevOps Fundamentals Demo', 'AWS Basics Demo']);
        setQuestionsLoading(false);
        loadingRef.current = false;
        return;
      }

      const [questionsResponse, assessmentsResponse] = await Promise.all([
        fetch(`${API_URL}/admin/questions`),
        fetch(`${API_URL}/admin/questions?type=assessments`)
      ]);
      
      if (!questionsResponse.ok) throw new Error(`HTTP ${questionsResponse.status}`);
      
      const questions = await questionsResponse.json();
      setAllQuestions(questions || []);
      
      const namesFromQuestions = [...new Set(questions.map(q => q.assessment_name).filter(name => name))];
      
      let namesFromConfigs = [];
      if (assessmentsResponse.ok) {
        const assessments = await assessmentsResponse.json();
        namesFromConfigs = assessments.map(a => a.name).filter(name => name);
      }
      
      const allNames = [...new Set([...namesFromQuestions, ...namesFromConfigs])];
      setAssessmentNames(allNames);
    } catch (error) {
      console.error('Error loading questions:', error);
      onMessage({ text: 'Error loading questions: ' + error.message, type: 'error' });
      setAllQuestions([]);
    } finally {
      setQuestionsLoading(false);
      loadingRef.current = false;
    }
  }, [user?.token, onMessage]);

  const loadCategoriesForAssessment = useCallback(async (assessmentName, questions) => {
    if (!assessmentName) {
      setAvailableCategories([]);
      setCategoriesLoading(false);
      return;
    }
    
    setCategoriesLoading(true);
    
    if (user?.token === 'demo-token-for-local-testing') {
      const demoCategories = {
        'Cloud Operator Demo': ['DevOps', 'Cloud Architecture', 'Security'],
        'DevOps Fundamentals Demo': ['CI/CD', 'DevOps', 'Automation'],
        'AWS Basics Demo': ['AWS', 'Cloud Services', 'Storage']
      };
      setAvailableCategories(demoCategories[assessmentName] || []);
      setCategoriesLoading(false);
      return;
    }
    
    const categoriesFromQuestions = [...new Set(questions
      .filter(q => q.assessment_name === assessmentName)
      .map(q => q.category)
      .filter(cat => cat))];
    
    let categoriesFromConfig = [];
    try {
      const response = await fetch(`${API_URL}/admin/questions?type=assessments`);
      if (response.ok) {
        const assessments = await response.json();
        const assessment = assessments.find(a => a.name === assessmentName);
        if (assessment && assessment.distribution) {
          categoriesFromConfig = Object.keys(assessment.distribution);
        }
      }
    } catch (error) {
      console.error('Error loading assessment config for categories:', error);
    }
    
    const allCategories = [...new Set([...categoriesFromQuestions, ...categoriesFromConfig])];
    setAvailableCategories(allCategories);
    setCategoriesLoading(false);
  }, [user?.token]);

  // Get all unique categories from filtered questions
  const allFilteredCategories = React.useMemo(() => {
    const questions = filterAssessment 
      ? allQuestions.filter(q => q.assessment_name === filterAssessment)
      : allQuestions;
    return [...new Set(questions.map(q => q.category).filter(cat => cat))].sort();
  }, [filterAssessment, allQuestions]);

  // Filter questions by assessment and category
  const filteredQuestions = React.useMemo(() => {
    let questions = allQuestions;
    if (filterAssessment) {
      questions = questions.filter(q => q.assessment_name === filterAssessment);
    }
    if (filterCategory) {
      questions = questions.filter(q => q.category === filterCategory);
    }
    return questions;
  }, [filterAssessment, filterCategory, allQuestions]);

  // Pagination calculations
  const totalPages = filteredQuestions.length === 0 ? 0 : Math.ceil(filteredQuestions.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedQuestions = filteredQuestions.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterAssessment, filterCategory]);

  useEffect(() => {
    loadCategoriesForAssessment(questionForm.assessment_name, allQuestions);
  }, [questionForm.assessment_name, allQuestions, loadCategoriesForAssessment]);

  useEffect(() => {
    loadQuestions();
  }, [loadQuestions]);

  // Memoize closeModal to prevent memory leak in event listener
  const closeModal = useCallback(() => {
    setShowModal(false);
    setEditingQuestion(null);
    // Restore preSelectedAssessment when closing modal
    setQuestionForm({ 
      id: '', 
      assessment_name: preSelectedAssessment || '', 
      category: '', 
      question: '', 
      choices: ['', '', '', ''], 
      correct_answer: '' 
    });
  }, [preSelectedAssessment]);

  // Keyboard accessibility - close modal on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && showModal) {
        closeModal();
      }
    };
    
    if (showModal) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [showModal, closeModal]);

  const openEditModal = (question) => {
    setEditingQuestion(question);
    setQuestionForm({
      id: question.id,
      assessment_name: question.assessment_name,
      category: question.category,
      question: question.question,
      choices: question.choices || ['', '', '', ''],
      correct_answer: question.correct_answer
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!questionForm.assessment_name || questionForm.assessment_name.trim() === '') {
      onMessage({ text: 'Validation Error: Assessment name is required', type: 'error' });
      return;
    }
    
    let questionId = questionForm.id;
    if (!questionId || questionId.trim() === '') {
      let assessmentPrefix = questionForm.assessment_name
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '')
        .substring(0, 8);
      
      if (!assessmentPrefix || assessmentPrefix.length === 0) {
        assessmentPrefix = 'question';
      }
      
      const timestamp = Date.now().toString();
      
      // Security: Use crypto.getRandomValues for cryptographically secure random IDs
      let random;
      try {
        const randomArray = new Uint32Array(3);
        crypto.getRandomValues(randomArray);
        random = Array.from(randomArray)
          .map(num => num.toString(36))
          .join('')
          .substring(0, 15);
      } catch (error) {
        // SECURITY WARNING: Fallback should only be used in non-production environments
        // In production, this should fail rather than use weak randomness
        console.error('SECURITY WARNING: crypto.getRandomValues not available. IDs may be predictable.');
        onMessage({ 
          text: 'Warning: Secure ID generation not available. Please use HTTPS.', 
          type: 'error' 
        });
        return;
      }
      
      const perfNow = Math.floor(performance.now() * 1000).toString(36);
      questionId = `${assessmentPrefix}_${timestamp}_${random}_${perfNow}`;
    }
    
    const sanitizedForm = {
      ...questionForm,
      id: questionId,
      question: sanitizeInput(questionForm.question),
      choices: questionForm.choices.map(choice => sanitizeInput(choice)),
      correct_answer: sanitizeInput(questionForm.correct_answer),
      assessment_name: sanitizeInput(questionForm.assessment_name),
      category: sanitizeInput(questionForm.category)
    };
    
    const validation = validateQuestion(sanitizedForm);
    if (!validation.isValid) {
      const errorMessages = Object.values(validation.errors).join(', ');
      onMessage({ text: 'Validation Error: ' + errorMessages, type: 'error' });
      return;
    }
    
    try {
      if (user?.token === 'demo-token-for-local-testing') {
        onMessage({ text: 'Demo: Question saved successfully!', type: 'success' });
        if (editingQuestion) {
          setAllQuestions(prev => prev.map(q => q.id === sanitizedForm.id ? sanitizedForm : q));
        } else {
          setAllQuestions(prev => [...prev, sanitizedForm]);
        }
        // Fix: Clear form state after demo mode save to prevent data leakage
        closeModal();
        return;
      }

      const response = await fetch(`${API_URL}/admin/questions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(user?.token ? { 'Authorization': `Bearer ${user.token}` } : {})
        },
        body: JSON.stringify(sanitizedForm)
      });
      
      // Error handling for JSON parsing
      let result;
      try {
        result = await response.json();
      } catch (jsonError) {
        console.error('JSON parsing error:', jsonError);
        onMessage({ 
          text: 'Error: Invalid response from server. Please try again.', 
          type: 'error' 
        });
        return;
      }
      
      if (response.ok) {
        onMessage({ text: editingQuestion ? 'Question updated successfully!' : 'Question saved successfully!', type: 'success' });
        closeModal();
        loadQuestions();
      } else {
        onMessage({ text: 'Error: ' + (result.error || 'Unknown error'), type: 'error' });
      }
    } catch (error) {
      console.error('Form submission error:', error);
      onMessage({ text: 'Error: ' + error.message, type: 'error' });
    }
  };

  const deleteQuestion = async (id) => {
    if (!window.confirm('Are you sure you want to delete this question?')) return;
    
    try {
      if (user?.token === 'demo-token-for-local-testing') {
        setAllQuestions(prev => prev.filter(q => q.id !== id));
        onMessage({ text: 'Demo: Question deleted successfully!', type: 'success' });
        return;
      }

      const response = await fetch(`${API_URL}/admin/questions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(user?.token ? { 'Authorization': `Bearer ${user.token}` } : {})
        },
        body: JSON.stringify({ action: 'delete', id })
      });
      
      // Error handling for JSON parsing
      let result;
      try {
        result = await response.json();
      } catch (jsonError) {
        console.error('JSON parsing error:', jsonError);
        onMessage({ 
          text: 'Error: Invalid response from server. Please try again.', 
          type: 'error' 
        });
        return;
      }
      
      if (response.ok) {
        onMessage({ text: 'Question deleted successfully!', type: 'success' });
        loadQuestions();
      } else {
        onMessage({ text: 'Error: ' + (result.error || 'Unknown error'), type: 'error' });
      }
    } catch (error) {
      onMessage({ text: 'Error: ' + error.message, type: 'error' });
    }
  };

  return (
    <div className="admin-program-management">
      <div className="card">
        <button 
          className="btn btn-primary" 
          onClick={() => setShowModal(true)}
          style={{marginBottom: '1rem'}}
        >
          + Add New Question
        </button>

        <div className="filter-controls" style={{marginBottom: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap'}}>
          <select value={filterAssessment} onChange={(e) => {
            setFilterAssessment(e.target.value);
            setFilterCategory(''); // Reset category filter when assessment changes
          }}>
            <option value="">All Assessments</option>
            {assessmentNames.map(name => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>
          
          <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
            <option value="">All Categories</option>
            {allFilteredCategories.map(category => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>
          
          <button className="btn btn-secondary" onClick={() => {
            setFilterAssessment('');
            setFilterCategory('');
          }}>Clear Filters</button>
          
          <button className="btn btn-secondary" onClick={loadQuestions} disabled={questionsLoading}>
            {questionsLoading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
        
        {questionsLoading && <div className="loading">Loading questions...</div>}
        
        {!questionsLoading && (
          <div style={{overflowX: 'auto'}}>
            {filteredQuestions.length === 0 ? (
              <p>No questions found. {allQuestions.length === 0 ? 'Add your first question!' : 'Try changing the filters.'}</p>
            ) : (
              <>
                <div style={{marginBottom: '0.75rem', color: '#6c757d', fontSize: '0.9em'}}>
                  Showing {startIndex + 1}-{Math.min(endIndex, filteredQuestions.length)} of {filteredQuestions.length} questions
                </div>
                <table style={{width: '100%', borderCollapse: 'collapse', fontSize: '0.9em'}}>
                  <thead>
                    <tr style={{backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6'}}>
                      <th style={{padding: '0.75rem', textAlign: 'left'}}>Assessment</th>
                      <th style={{padding: '0.75rem', textAlign: 'left'}}>Category</th>
                      <th style={{padding: '0.75rem', textAlign: 'left'}}>Question</th>
                      <th style={{padding: '0.75rem', textAlign: 'center'}}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedQuestions.map(q => (
                      <tr key={q.id} style={{borderBottom: '1px solid #dee2e6'}}>
                        <td style={{padding: '0.75rem'}}>{q.assessment_name || 'N/A'}</td>
                        <td style={{padding: '0.75rem'}}>{q.category || 'No Category'}</td>
                        <td style={{padding: '0.75rem', maxWidth: '400px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                          {q.question}
                        </td>
                        <td style={{padding: '0.75rem', textAlign: 'center'}}>
                          <button 
                            className="btn btn-primary" 
                            onClick={() => openEditModal(q)}
                            style={{marginRight: '0.5rem', padding: '0.25rem 0.75rem', fontSize: '0.85em'}}
                          >
                            Edit
                          </button>
                          <button 
                            className="btn btn-danger" 
                            onClick={() => deleteQuestion(q.id)}
                            style={{padding: '0.25rem 0.75rem', fontSize: '0.85em'}}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                
                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '0.5rem',
                    marginTop: '1.5rem',
                    flexWrap: 'wrap'
                  }}>
                    <button
                      className="btn btn-secondary"
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      style={{padding: '0.5rem 1rem', fontSize: '0.9em'}}
                      aria-label="Go to previous page"
                    >
                      Previous
                    </button>
                    
                    {/* Page numbers */}
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => {
                      // Show first page, last page, current page, and pages around current
                      const showPage = pageNum === 1 || 
                                      pageNum === totalPages || 
                                      Math.abs(pageNum - currentPage) <= 2;
                      
                      // Show ellipsis for gaps between visible page ranges
                      // Ellipsis before: between page 1 and the current page range
                      const showEllipsisBefore = pageNum === 2 && currentPage > 4 && !showPage;
                      // Ellipsis after: between the current page range and last page
                      const showEllipsisAfter = pageNum === totalPages - 1 && currentPage < totalPages - 3 && !showPage;
                      
                      if (showEllipsisBefore) {
                        return <span key={`ellipsis-before-${pageNum}`} style={{padding: '0.5rem'}} aria-hidden="true">...</span>;
                      }
                      
                      if (showEllipsisAfter) {
                        return <span key={`ellipsis-after-${pageNum}`} style={{padding: '0.5rem'}} aria-hidden="true">...</span>;
                      }
                      
                      if (!showPage) return null;
                      
                      return (
                        <button
                          key={pageNum}
                          className={pageNum === currentPage ? 'btn btn-primary' : 'btn btn-secondary'}
                          onClick={() => setCurrentPage(pageNum)}
                          style={{
                            padding: '0.5rem 0.75rem',
                            fontSize: '0.9em',
                            minWidth: '40px',
                            fontWeight: pageNum === currentPage ? 'bold' : 'normal'
                          }}
                          aria-label={`Go to page ${pageNum}`}
                          aria-current={pageNum === currentPage ? 'page' : undefined}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    
                    <button
                      className="btn btn-secondary"
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      style={{padding: '0.5rem 1rem', fontSize: '0.9em'}}
                      aria-label="Go to next page"
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Modal for Add/Edit Question */}
      {showModal && (
        <div 
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem'
          }}
          onClick={(e) => {
            // Close modal when clicking backdrop (not the modal content)
            if (e.target === e.currentTarget) {
              closeModal();
            }
          }}
        >
          <div 
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '2rem',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}
            onClick={(e) => e.stopPropagation()} // Prevent backdrop click when clicking inside modal
          >
            <h3 id="modal-title">{editingQuestion ? 'Edit Question' : 'Add New Question'}</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Question ID (optional - auto-generated if empty):</label>
                <input
                  type="text"
                  value={questionForm.id}
                  onChange={(e) => setQuestionForm({...questionForm, id: e.target.value})}
                  placeholder="Leave empty for auto-generation"
                  disabled={!!editingQuestion}
                />
                {!editingQuestion && (
                  <small style={{color: '#6c757d', fontSize: '0.85em', display: 'block', marginTop: '4px'}}>
                    Auto-generated based on assessment name
                  </small>
                )}
              </div>
              
              <div className="form-group">
                <label>Assessment Name:</label>
                <select
                  value={questionForm.assessment_name}
                  onChange={(e) => setQuestionForm({...questionForm, assessment_name: e.target.value})}
                  required
                >
                  <option value="">Select Assessment...</option>
                  {assessmentNames.map(name => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label>Category:</label>
                <select
                  value={questionForm.category}
                  onChange={(e) => setQuestionForm({...questionForm, category: e.target.value})}
                  required
                  disabled={categoriesLoading || !questionForm.assessment_name}
                >
                  <option value="">
                    {categoriesLoading ? 'Loading...' : 
                     !questionForm.assessment_name ? 'Select assessment first...' : 
                     'Select Category...'}
                  </option>
                  {availableCategories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label>Question:</label>
                <textarea
                  value={questionForm.question}
                  onChange={(e) => setQuestionForm({...questionForm, question: e.target.value})}
                  placeholder="Enter your question here..."
                  required
                  rows="3"
                />
              </div>
              
              <div className="form-group">
                <label>Answer Choices:</label>
                {questionForm.choices.map((choice, i) => (
                  <input
                    key={i}
                    type="text"
                    value={choice}
                    onChange={(e) => {
                      const newChoices = [...questionForm.choices];
                      newChoices[i] = e.target.value;
                      setQuestionForm({...questionForm, choices: newChoices});
                    }}
                    placeholder={`Choice ${i + 1}`}
                    required
                    style={{marginBottom: '0.5rem'}}
                  />
                ))}
              </div>
              
              <div className="form-group">
                <label>Correct Answer:</label>
                <input
                  type="text"
                  value={questionForm.correct_answer}
                  onChange={(e) => setQuestionForm({...questionForm, correct_answer: e.target.value})}
                  placeholder="Enter the exact correct answer"
                  required
                />
              </div>
              
              <div style={{display: 'flex', gap: '0.5rem', marginTop: '1.5rem'}}>
                <button type="submit" className="btn btn-primary">
                  {editingQuestion ? 'Update Question' : 'Save Question'}
                </button>
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={closeModal}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminQuestionManagement;
